import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Routes } from '@/router';
import { OrderWithContext, OrderType, FormType } from '@/models';
import { FooterMode, CountryCode } from '@/store/ui';
import defer from 'lodash/defer';
import { AnalyticsEventAction, AnalyticsEventCategory, AnalyticsEventLabel } from '@/services/google/google-analytics.services';

@Component({
  name: 'CmgGroupOrderCreatePage',
  beforeRouteLeave(to, from, next) {
    this.$services.ui.popFooterState();
    next();
  },
})
export default class CmgGroupOrderCreatePage extends Vue {
  isCustomerInitialized = false;
  message = '';
  isLoading = false;
  readonly maxMessageLength = 200;
  isLoggedIn = false;
  explicitGroupOrderClick = false;
  
  watchers: (() => void)[] = [];

  get messageLengthRemaining(): number {
    return this.maxMessageLength - this.message.length;
  }

  get cateringUrl(): string {
    return this.$services.config.getCateringUrl();
  }

  get dancingVeggiesUrl() {
    return this.$appStore.state.cms.images.dancingVeggies.default;
  }

  get shouldShowCateringLink(): boolean {
    switch (this.$appStore.state.ui.countryProfile.countryCode) {
      case CountryCode.CA:
        return false;
      case CountryCode.US:
      default:
        return true;
    }
  }

  async mounted() {
    this.$services.ui.nextFocus('create-group-order');
    this.$services.ui.pushFooterState({ mode: FooterMode.None });
    this.isLoggedIn = await this.$services.customer.waitForCustomerInitialization();
    this.watchers.push(this.$appStore.watch(state => state.customer.profile, this.customerLoggedIn));
    this.isCustomerInitialized = true;

    const order = this.$services.order.getCurrentOrder();
    if (order && order.order.orderType == OrderType.Group) {
      this.$router.push({ name: Routes.Home });
      this.$services.ui.setLastSelectedElement('bag');
      this.$services.bag.setBagVisibility(true);
      return;
    }

    this.$services.bag.setBagVisibility(false);
  }

  destroyed() {
    for (const unsubFn of this.watchers) {
      unsubFn();
    }
  }

  onCreateOrderClick() {
    this.explicitGroupOrderClick = true;
    if (!this.isLoggedIn) {
      this.$services.ui.setModalVisibility('signIn', true, {
        type: FormType.GroupOrderSignIn,
        route: Routes.GroupOrderCreate,
      });
    } else {
      this.attemptOrder();
    }
  }

  customerLoggedIn() {
    if(this.$appStore.state.customer.profile) {
      this.isLoggedIn = true;
      this.attemptOrder();
    }
  }

  attemptOrder() {
    if(this.explicitGroupOrderClick) {
      if(this.$appStore.state.customer.profile && this.checkForSelectedRestaurant()) {
        this.createOrder();
      }
    }
  }

  checkForSelectedRestaurant(): boolean {
    if (!this.$appStore.state.restaurant.selectedRestaurant) {
      this.$services.ui.setModalVisibility('findAChipotle', true, { onClose: this.createOrder });
      this.$services.ui.setModalVisibility('toast', true, {
        body: 'Before you order, we gotta find your Chipotle first.'.toUpperCase(),
        bodyOnly: true,
      });
      return false;
    }
    return true;
  }

  async createOrder() {
    if (!this.$appStore.state.restaurant.selectedRestaurant) {
      this.$services.logger.unreachable('CmgGroupOrderCreatePage->createOrder() restaurant was selected but still NULL');
      return;
    }

    this.isLoading = true;

    try {
      const currentOrder = this.$services.order.getCurrentOrder();
      let updatedOrder: OrderWithContext | null;

      if (currentOrder && currentOrder.order.meals && currentOrder.order.meals.length) {
        updatedOrder = await this.$services.order.convertOrderToGroupOrder(currentOrder);
        this.$services.order.setPendingOrder(null);
      } else {
        updatedOrder = await this.$services.order.createGroupOrder(this.$appStore.state.restaurant.selectedRestaurant.restaurantNumber, this.message);
      }

      // TODO-pvq54s: Handle exceptions when we have direction on what to do with said exceptions
      if (!updatedOrder) {
        this.$services.logger.error('Creating group order failed for unknown reasons');
      }

      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.Behavior,
        action: AnalyticsEventAction.Click,
        label: AnalyticsEventLabel.CreateGroupOrder,
        values: { customEventGroupOrderId: updatedOrder?.order.groupOrderId ?? '' },
      });

      this.$services.order.setIsCustomerHostOfGroupOrder(true);
      this.$services.order.setPendingGroupOrder(updatedOrder, '');
      this.$services.order.pollGroupOrderIfExists();
      this.$router.push({ name: Routes.Order, hash: '#menu' });
      this.$services.ui.setLastSelectedElement('bag');
      this.$services.bag.setBagVisibility(true);
      this.$nextTick(() => defer(() => this.$services.ui.focusDiv('.fullModal')));
    } finally {
      this.isLoading = false;
    }
  }
}
